<template>
  <div class="w-full">
    <!-- Header -->
    <div v-if="showTitle" class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-3">
        <h3 class="text-sm font-bold text-gray-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
          <ExclamationTriangleIcon class="size-5 text-amber-500" />
          Alertas del Paciente
        </h3>
      </div>
      <div class="flex items-center gap-2">
        <slot name="actions" />
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="visibleAlertas.length === 0"
      class="text-center py-10 px-6 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/30 transition-all duration-300"
    >
      <p class="text-sm text-gray-500 font-medium font-inter">No hay alertas disponibles para este paciente.</p>
    </div>

    <!-- Alerts list with Grid -->
    <div class="space-y-6">
      <section v-for="section in alertSections" :key="section.key">
        <div class="flex items-center gap-3 mb-3">
          <h4 class="text-sm font-bold text-gray-700 dark:text-gray-200">{{ section.title }}</h4>
          <span class="h-px flex-1 bg-gray-200 dark:bg-gray-700"></span>
        </div>
        <TransitionGroup
          tag="div"
          enter-active-class="transition-all duration-500 ease-out"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition-all duration-300 ease-in absolute"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
          class="grid grid-cols-1 lg:grid-cols-2 gap-4"
        >
        <div v-for="(alert, index) in section.alerts" :key="`alert-${alert.alerId}-${index}`" class="group">
        <div
          class="h-full relative overflow-hidden rounded-xl transition-all duration-300 hover:shadow-lg border border-gray-100 dark:border-gray-800"
          :class="[getAlertCardBackground(alert), getAlertCardBorder(alert)]"
        >
          <!-- Subtle hover glass effect -->
          <div class="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          <!-- Content Wrapper -->
          <div class="relative p-4">
            <div class="flex gap-4">
              <!-- Icon Circle - Minimalist -->
              <div
                class="shrink-0 w-10 h-10 rounded-full flex items-center justify-center border shadow-sm"
                :class="getAlertIconClasses(alert)"
              >
                <component :is="getAlertIcon(alert)" class="w-5 h-5" />
              </div>

              <!-- Main Info -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between gap-2 mb-1">
                  <div class="flex items-center gap-2 overflow-hidden">
                    <h3 class="text-[11px] font-black uppercase tracking-widest truncate" :class="getAlertTitleClass(alert)">
                      {{ getAlertTitle(alert) }}
                    </h3>
                    <span
                      v-if="expiredIds.has(alert.alerId as number)"
                      class="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-900 text-white uppercase tracking-tighter"
                    >
                      Vencida
                    </span>
                  </div>
                  <div class="flex items-center gap-2">
                    <ButtonList
                      v-if="tieneAcciones(alert)"
                      :id="`alert-actions-${alert.alerId}`"
                      button-icon="Cog6ToothIcon"
                      dropdown-width="w-56"
                      :items="accionesAlerta(alert)"
                      color="gray-dark"
                    />
                    <slot name="actions" :alert="alert"></slot>
                  </div>
                </div>

                <p class="text-[13px] font-semibold text-gray-800 dark:text-gray-200 leading-tight mb-3">
                  {{ getAlertNombre(alert) }}
                </p>

                <!-- Servicio detail -->
                <div
                  v-if="alert.servicio && alert.tipoAlerta"
                  class="mb-3 p-2.5 rounded-lg bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5"
                >
                  <div class="flex items-center gap-2 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-tight mb-1">
                    <DocumentTextIcon class="w-3.5 h-3.5" />
                    SERVICIO: {{ alert.servicio.coseNombre }}
                  </div>
                </div>

                <!-- Description Toggle -->
                <div v-if="alert.alerDetalle" class="mb-3">
                  <button
                    v-if="alert.alerId"
                    class="cursor-pointer flex items-center gap-1.5 text-[11px] font-bold text-gray-500 hover:text-black dark:hover:text-white transition-colors uppercase"
                    @click="toggleDetails(alert.alerId)"
                  >
                    <ChevronRightIcon class="w-3.5 h-3.5 transition-transform duration-300" :class="{ 'rotate-90': expandedAlerts.has(alert.alerId) }" />
                    Detalles
                  </button>

                  <Transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 max-h-0"
                    enter-to-class="opacity-100 max-h-96"
                    leave-active-class="transition-all duration-200 ease-in"
                    leave-from-class="opacity-100 max-h-96"
                    leave-to-class="opacity-0 max-h-0"
                  >
                    <div v-if="alert.alerId && expandedAlerts.has(alert.alerId)" class="mt-2 text-xs text-gray-600 dark:text-gray-400 leading-relaxed pl-5 border-l border-gray-200 dark:border-gray-700">
                      {{ alert.alerDetalle }}
                    </div>
                  </Transition>
                </div>

                <!-- Footer -->
                <div class="flex items-center gap-4 pt-3 border-t border-gray-100 dark:border-gray-800">
                  <div class="flex items-center gap-1.5 text-[10px] text-gray-400 font-medium">
                    <CalendarIcon class="w-3.5 h-3.5" />
                    {{ formatearFechaEnEspanol(alert.createdAt as string) }}
                  </div>
                  <div class="flex items-center gap-1.5 text-[10px] font-bold" :class="(alert.alerId && expiredIds.has(alert.alerId)) ? 'text-red-500' : 'text-gray-400'">
                    <ClockIcon class="w-3.5 h-3.5" />
                    {{ alert.alerFecha ? formatearFechaEnEspanol(alert.alerFecha) : 'Sin vencimiento' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
        </TransitionGroup>
      </section>
    </div>

    <BtnCerrarAlerta
      v-if="alertaACerrar"
      :key="alertaACerrar.alerId"
      ref="cerrarAlertaRef"
      :alerta="alertaACerrar"
      :render-trigger="false"
      :cmot-tipo="props.cmotTipo"
      :citt-id-context="props.cittIdContext"
      @closed="handleAlertaCerrada"
    />
  </div>
</template>

<script setup lang="ts">
import { useAlerts, groupAlertasForDisplay } from '../../composables/useAlerts'
import { toRef, computed, ref, nextTick } from 'vue'
import type { IAlertAlertas } from '@/modules/Asistencial/interfaces'
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  CalendarIcon,
  ChevronRightIcon,
  ClockIcon
} from '@heroicons/vue/24/outline'
import { formatearFechaEnEspanol } from '@/modules/Core/usings/generales'
import ButtonList from '@/modules/Core/components/general/ButtonList.vue'
import BtnCerrarAlerta from '@/modules/DemandaInducida/Components/BtnCerrarAlerta.vue'
import { useTrazabilidadRutaPdf } from '@/modules/DemandaInducida/composables/useTrazabilidadRutaPdf'

interface Props {
  coppId: number
  alertas: IAlertAlertas[]
  filterByFecha?: boolean
  showTitle?: boolean
  showCloseButton?: boolean
  cmotTipo?: string
  cittIdContext?: number
}

const props = withDefaults(defineProps<Props>(), {
  showCloseButton: false,
  cmotTipo: 'CIERRE ALERTA',
  cittIdContext: undefined
})

const emit = defineEmits<{
  (e: 'alerta-cerrada'): void
}>()

const { generarPdf: generarPdfTrazabilidad } = useTrazabilidadRutaPdf()

const esAlertaRuta = (alert: IAlertAlertas): boolean => {
  return !!alert.cittId && alert.tablaTipoAlerta?.comaNombrecorto === 'TBL_TIPORUTAS'
}

const accionesAlerta = (alert: IAlertAlertas) => [
  {
    label: 'Descargar PDF',
    icon: 'DocumentArrowDownIcon',
    show: esAlertaRuta(alert),
    clickEvent: () => generarPdfTrazabilidad(alert)
  },
  {
    label: 'Cerrar alerta',
    icon: 'XMarkIcon',
    show: props.showCloseButton && alert.alerEstado === 1,
    clickEvent: () => abrirCierreAlerta(alert)
  }
]
const tieneAcciones = (alert: IAlertAlertas): boolean =>
  accionesAlerta(alert).some((a) => a.show !== false)

const alertaACerrar = ref<IAlertAlertas | null>(null)
const cerrarAlertaRef = ref<InstanceType<typeof BtnCerrarAlerta> | null>(null)

const abrirCierreAlerta = async (alert: IAlertAlertas) => {
  alertaACerrar.value = alert
  await nextTick()
  await cerrarAlertaRef.value?.abrir()
}

const handleAlertaCerrada = () => {
  emit('alerta-cerrada')
  alertaACerrar.value = null
}

// Usar solo las funciones necesarias del composable
const { getUnifiedStyle, getAlertNombre } = useAlerts({
  coppId: toRef(props, 'coppId')
})

const expandedAlerts = ref<Set<number>>(new Set())

const alertGroups = computed(() => groupAlertasForDisplay(props.alertas, props.filterByFecha === false))
const visibleAlertas = computed(() => [
  ...alertGroups.value.active,
  ...alertGroups.value.inactive
])
const expiredIds = computed(() => alertGroups.value.expiredIds)
const alertSections = computed(() => [
  { key: 'active', title: 'Alertas activas', alerts: alertGroups.value.active },
  { key: 'inactive', title: 'Alertas cerradas', alerts: alertGroups.value.inactive }
].filter(section => section.alerts.length > 0))

const toggleDetails = (alerId: number) => {
  if (expandedAlerts.value.has(alerId)) {
    expandedAlerts.value.delete(alerId)
  } else {
    expandedAlerts.value.add(alerId)
  }
}

// Logic for Redesigned Card
const getAlertCardBackground = (alert: IAlertAlertas) => {
  if (!alert.alerId) return 'bg-gray-50 dark:bg-gray-800'

  const isExpired = expiredIds.value.has(alert.alerId as number)
  if (isExpired) return 'bg-red-50 dark:bg-red-900/20'

  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.background || 'bg-gray-50 dark:bg-gray-800'
}

const getAlertCardBorder = (alert: IAlertAlertas) => {
  if (!alert.alerId) return 'border-gray-200 dark:border-gray-700'

  const isExpired = expiredIds.value.has(alert.alerId as number)
  if (isExpired) return 'border-red-200 dark:border-red-800 shadow-red-100/50'

  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.border || 'border-gray-200 dark:border-gray-700'
}

// const getAlertAccentBarClass = (alert: IAlertAlertas) => {
//   const isExpired = expiredIds.value.has(alert.alerId)
//   if (isExpired) return 'bg-red-500'

//   const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
//   switch (style.color) {
//     case 'purple': return 'bg-purple-500'
//     case 'teal': return 'bg-teal-500'
//     case 'sky': return 'bg-sky-500'
//     case 'indigo': return 'bg-indigo-500'
//     default: return 'bg-blue-500'
//   }
// }

const getAlertTitle = (alert: IAlertAlertas) => {
  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.title
}

const getAlertTitleClass = (alert: IAlertAlertas) => {
  const isExpired = expiredIds.value.has(alert.alerId as number)
  if (isExpired) return 'text-red-700 dark:text-red-400'

  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.titleColor
}

const getAlertIconClasses = (alert: IAlertAlertas) => {
  const isExpired = expiredIds.value.has(alert.alerId as number)
  if (isExpired) return 'bg-red-100 text-red-600 border-red-200 dark:bg-red-900/40 dark:text-red-400 dark:border-red-800'

  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.iconClass || 'bg-gray-100 text-gray-500 border-gray-200'
}

const getAlertIcon = (alert: IAlertAlertas) => {
  const isExpired = expiredIds.value.has(alert.alerId as number)
  if (isExpired) return XCircleIcon

  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.icon || InformationCircleIcon
}
</script>
